<template>
  <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
    <div class="bg-white rounded-xl shadow-2xl w-[98vw] h-[95vh] flex overflow-hidden">
      <!-- Left Side Panel -->
      <div 
        :class="[
          'bg-gray-50 border-r transition-all duration-300 flex flex-col',
          showSidepanel ? 'w-80' : 'w-0'
        ]"
      >
        <!-- Side Panel Header -->
        <div class="p-4 border-b bg-white">
          <div class="flex items-center justify-between">
            <h3 class="font-semibold text-gray-800">Fixtures</h3>
            <button 
              @click="showSidepanel = false"
              class="w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Side Panel Content -->
        <div class="flex-1 overflow-y-auto flex flex-col">
          <!-- Loading State -->
          <div v-if="loadingFixture" class="flex items-center justify-center h-32">
            <div class="text-center">
              <i class="fas fa-spinner fa-spin text-xl text-blue-500 mb-2"></i>
              <p class="text-sm text-gray-600">Loading fixtures...</p>
            </div>
          </div>

          <!-- Filters Section -->
          <div v-if="!loadingFixture && fixturesList.length > 0" class="p-3 border-b bg-white sticky top-0 z-10">
            <div class="space-y-2">
              <!-- Search Filter -->
              <div>
                <input
                  v-model="fixtureFilters.search"
                  type="text"
                  placeholder="Search by ID, team, league..."
                  class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <!-- Status Filter -->
              <div>
                <select
                  v-model="fixtureFilters.status"
                  class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="all">All Status</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
              
              <!-- League Filter -->
              <div v-if="uniqueLeagues.length > 0">
                <select
                  v-model="fixtureFilters.league"
                  class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="all">All Leagues</option>
                  <option v-for="league in uniqueLeagues" :key="league.slug" :value="league.slug">
                    {{ league.name }}
                  </option>
                </select>
              </div>
              
              <!-- Results Count -->
              <div class="text-xs text-gray-500 pt-1 border-t">
                Showing {{ filteredFixturesList.length }} of {{ fixturesList.length }} fixtures
              </div>
              
              <!-- Clear Filters Button -->
              <button
                v-if="fixtureFilters.status !== 'all' || fixtureFilters.search || fixtureFilters.league !== 'all'"
                @click="fixtureFilters = { status: 'all', search: '', league: 'all' }"
                class="w-full px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                <i class="fas fa-times mr-1"></i>
                Clear Filters
              </button>
            </div>
          </div>

          <!-- Fixtures List -->
          <div v-if="!loadingFixture && fixturesList.length > 0" class="flex-1 overflow-y-auto p-2">
            <div 
              v-for="fixture in filteredFixturesList" 
              :key="fixture.id"
              :class="[
                'p-3 rounded-lg mb-2 cursor-pointer transition-all hover:shadow-sm',
                fixture.id == currentFixtureId 
                  ? 'bg-green-100 border border-green-300' 
                  : fixture.id == props.fixtureId
                  ? 'bg-blue-100 border border-blue-300'
                  : 'bg-white border border-gray-200 hover:border-gray-300'
              ]"
              @click="selectFixture(fixture)"
            >
              <!-- Fixture Header -->
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-semibold text-gray-900">#{{ fixture.id }}</span>
                <div class="flex items-center gap-1">
                  <span :class="[
                    'text-xs px-2 py-1 rounded-full font-medium',
                    fixture.published_at ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  ]">
                    {{ fixture.published_at ? 'Published' : 'Draft' }}
                  </span>
                  <span v-if="fixture.id == currentFixtureId" class="text-xs bg-green-200 text-green-800 px-2 py-1 rounded-full font-medium">
                    Viewing
                  </span>
                  <span v-else-if="fixture.id == props.fixtureId" class="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded-full font-medium">
                    Original
                  </span>
                </div>
              </div>

              <!-- Teams -->
              <div class="mb-2">
                <div v-if="fixture.home_team && fixture.away_team" class="text-sm text-gray-700">
                  <div class="font-medium">{{ fixture.home_team.team_name }}</div>
                  <div class="text-xs text-gray-500 text-center">vs</div>
                  <div class="font-medium">{{ fixture.away_team.team_name }}</div>
                </div>
                <div v-else class="text-sm text-gray-400">No teams data</div>
              </div>

              <!-- League & Date -->
              <div class="space-y-1">
                <div v-if="fixture.league" class="text-xs">
                  <div class="text-gray-900 font-medium">{{ fixture.league.league_name }}</div>
                  <div class="text-gray-500">{{ fixture.league.slug }}</div>
                </div>
                <div class="text-xs text-gray-600">
                  {{ fixture.gamedate }}
                </div>
                <div class="text-xs text-gray-500">
                  {{ fixture.user_data?.name || 'Unknown' }}
                </div>
              </div>
            </div>
          </div>

          <!-- No Results Message -->
          <div v-if="!loadingFixture && fixturesList.length > 0 && filteredFixturesList.length === 0" class="flex-1 flex items-center justify-center p-4">
            <div class="text-center">
              <i class="fas fa-filter text-3xl text-gray-300 mb-3"></i>
              <p class="text-sm text-gray-500 mb-2">No fixtures match your filters</p>
              <button
                @click="fixtureFilters = { status: 'all', search: '', league: 'all' }"
                class="px-3 py-1.5 text-xs bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="!loadingFixture" class="p-4 text-center">
            <i class="fas fa-exclamation-triangle text-2xl text-red-500 mb-2"></i>
            <p class="text-sm text-red-600 mb-2">Failed to load fixtures</p>
            <button @click="loadFixtureDetails" class="px-3 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600">
              Try Again
            </button>
          </div>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="flex-1 flex flex-col overflow-hidden">
      <!-- Header -->
      <div class="bg-gray-50 border-b">
        <!-- Main Header Row -->
        <div class="flex items-center justify-between p-4">
          <!-- Left: Title and Fixture Info -->
          <div class="flex items-center gap-6">
            <div>
              <h2 class="text-xl font-semibold text-gray-800">Fixture Player Lines Timeline</h2>
              <div class="text-sm text-gray-500 mt-1">Fixture ID: {{ currentFixtureId }}</div>
            </div>
            
            <!-- Fixture Details -->
            <div v-if="fixtureData.home_team && fixtureData.away_team" class="flex items-center gap-4">
              <div class="text-sm">
                <div class="font-medium text-gray-800">
                  {{ fixtureData.home_team.team_name }} vs {{ fixtureData.away_team.team_name }}
                </div>
                <div v-if="fixtureData.gamedate" class="text-xs text-gray-500 mt-1">
                  {{ fixtureData.gamedate }}
                </div>
              </div>
              
              <div v-if="fixtureData.league" class="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                {{ fixtureData.league.league_name }}
              </div>
              
              <div v-if="currentFixtureId !== fixtureId" class="text-xs bg-orange-100 text-orange-800 px-3 py-1 rounded-full">
                Viewing Different Fixture
              </div>
            </div>
          </div>
          
          <!-- Right: Action Buttons -->
          <div class="flex items-center gap-3">
            <button 
              @click="showSidepanel = !showSidepanel"
              :class="[
                'px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors',
                showSidepanel 
                  ? 'bg-gray-200 hover:bg-gray-300 text-gray-700' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
              ]"
            >
              <i class="fas fa-list"></i>
              {{ showSidepanel ? 'Hide' : 'Show' }} Fixtures
            </button>
            <button 
              @click="showFixtureDetails = true"
              class="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm flex items-center gap-2 transition-colors"
            >
              <i class="fas fa-info-circle"></i>
              Fixture Details
            </button>
            <button 
              v-if="currentFixtureId !== fixtureId"
              @click="resetToOriginalFixture"
              class="px-4 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-lg text-sm flex items-center gap-2 transition-colors"
            >
              <i class="fas fa-undo"></i>
              Reset to Original
            </button>
            <button @click="$emit('close')" class="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        
        <!-- Controls Row -->
        <div class="px-4 pb-4">
          <div class="flex items-center justify-between">
            <!-- Left: Date Filters -->
            <div class="flex items-center gap-3">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-700">Date Range:</label>
                <input 
                  v-model="filters.startDate" 
                  type="date" 
                  class="px-3 py-2 border rounded-lg text-sm"
                  placeholder="Start Date"
                />
                <span class="text-gray-400">to</span>
                <input 
                  v-model="filters.endDate" 
                  type="date" 
                  class="px-3 py-2 border rounded-lg text-sm"
                  placeholder="End Date"
                />
              </div>
              
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-700">Target Time:</label>
                <input 
                  v-model="filters.targetTime" 
                  type="datetime-local" 
                  class="px-3 py-2 border rounded-lg text-sm"
                  placeholder="Target Time"
                />
                <button 
                  v-if="filters.targetTime"
                  @click="filters.targetTime = ''"
                  class="px-2 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm"
                  title="Clear target time"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
              
              <button 
                @click="loadTimelineData" 
                class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm flex items-center gap-2"
                :disabled="loading"
              >
                <i v-if="loading" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-sync-alt"></i>
                {{ filters.targetTime ? 'Find Closest Time' : 'Refresh' }}
              </button>
            </div>
            
            <!-- Right: Toggle Controls -->
            <div class="flex items-center gap-6">
              <!-- Show Changes Toggle -->
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-600">Show All</span>
                <button 
                  @click="showOnlyDifferentValues = !showOnlyDifferentValues"
                  :class="[
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
                    showOnlyDifferentValues ? 'bg-green-500' : 'bg-gray-300'
                  ]"
                  :title="showOnlyDifferentValues ? 'Show all values including duplicates' : 'Show only entries where balance line or odds changed'"
                >
                  <span
                    :class="[
                      'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                      showOnlyDifferentValues ? 'translate-x-6' : 'translate-x-1'
                    ]"
                  />
                </button>
                <span class="text-sm text-gray-600">Changes Only</span>
              </div>
              
              <!-- Timeline Toggle -->
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-600">Hide Timeline</span>
                <button 
                  @click="showSidepanel = !showSidepanel"
                  :class="[
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
                    showSidepanel ? 'bg-green-500' : 'bg-gray-300'
                  ]"
                  :title="showSidepanel ? 'Hide timestamp sidepanel' : 'Show timestamp sidepanel'"
                >
                  <span
                    :class="[
                      'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                      showSidepanel ? 'translate-x-6' : 'translate-x-1'
                    ]"
                  />
                </button>
                <span class="text-sm text-gray-600">Show Timeline</span>
              </div>
              
              <!-- Published Logs Toggle -->
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-600">All Logs</span>
                <button 
                  @click="toggleLogsFromPublished"
                  :class="[
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
                    showLogsFromPublished ? 'bg-green-500' : 'bg-gray-300'
                  ]"
                  :title="showLogsFromPublished ? 'Show all logs' : 'Show logs starting from published_at date to see what clients get'"
                >
                  <span
                    :class="[
                      'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                      showLogsFromPublished ? 'translate-x-6' : 'translate-x-1'
                    ]"
                  />
                </button>
                <span class="text-sm text-gray-600">From Published</span>
              </div>
              
              <!-- Action Buttons -->
              <div class="flex items-center gap-2">
                <button 
                  @click="openGlobalChart"
                  class="px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors bg-green-500 text-white hover:bg-green-600"
                  title="View global chart of all players and markets"
                >
                  <i class="fas fa-chart-line"></i>
                  Global Chart
                </button>
                
                <button 
                  v-if="filters.targetTime"
                  @click="applyTargetTimeFilter"
                  :class="[
                    'px-4 py-2 text-white rounded-lg text-sm transition-colors flex items-center gap-2',
                    showTargetTimeData 
                      ? 'bg-orange-600 hover:bg-orange-700' 
                      : 'bg-orange-500 hover:bg-orange-600'
                  ]"
                  :disabled="loading"
                >
                  <i :class="showTargetTimeData ? 'fas fa-eye-slash' : 'fas fa-filter'"></i>
                  {{ showTargetTimeData ? 'Hide Target Time Data' : 'Highlight Target Time Data' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Timeline Grid -->
      <div class="flex-1 overflow-hidden flex">
        <!-- Left Sidebar - Timestamps -->
        <div v-if="showSidepanel" class="w-80 flex-shrink-0 bg-gray-50 border-r border-gray-200 p-4 flex flex-col">
          <h3 class="text-lg font-semibold text-gray-800 mb-4">Timestamps</h3>
          
          <!-- Current Timestamp Info -->
          <div class="bg-white rounded-lg p-4 shadow-sm mb-4">
            <div class="flex items-center justify-between mb-2">
              <div class="text-sm text-gray-500">Current Selection</div>
              <div v-if="filters.targetTime" class="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                Target Time Filter Active
              </div>
            </div>
            <div class="text-sm font-medium text-gray-800">{{ formatDateTimeFull(currentTimestamp) }}</div>
            <div class="text-xs text-gray-500 mt-1">
              {{ currentTimestampIndex + 1 }} of {{ availableTimestamps.length }}
            </div>
            <div v-if="filters.targetTime" class="text-xs text-orange-600 mt-1">
              Target: {{ formatDateTimeFull(filters.targetTime) }}
            </div>
          </div>
          
          <!-- Navigation Controls -->
          <div class="flex items-center gap-2 mb-4">
            <button 
              @click="previousTimestamp" 
              :disabled="currentTimestampIndex <= 0"
              class="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="fas fa-chevron-left"></i>
            </button>
            
            <button 
              @click="nextTimestamp" 
              :disabled="currentTimestampIndex >= availableTimestamps.length - 1"
              class="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
          
          <!-- Timestamp Scrubber -->
          <div class="mb-4">
            <input 
              type="range" 
              :min="0" 
              :max="availableTimestamps.length - 1" 
              v-model="currentTimestampIndex"
              class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
          
          <!-- Available Timestamps List -->
           
          <div class="space-y-1 flex-1 overflow-y-auto">
            <button 
              v-for="(timestamp, index) in availableTimestamps" 
              :key="index"
              @click="jumpToTimestamp(index)"
              :class="[
                'w-full text-left px-3 py-2 rounded-lg text-sm transition-colors',
                index === currentTimestampIndex 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              ]"
            >
              <div class="font-medium">{{ formatDateTimeFull(timestamp) }}</div>
              <div class="text-xs opacity-75">{{ formatTime(timestamp) }}</div>
            </button>
          </div>
        </div>

        <!-- Main Content Area -->
        <div class="flex-1 overflow-auto p-4 bg-gray-200">
          <!-- Horizontal scrollable container for the table -->
          <div class="overflow-x-auto">
          <!-- Loading State -->
          <div v-if="loading" class="flex items-center justify-center h-64">
            <div class="text-center">
              <i class="fas fa-spinner fa-spin text-2xl text-blue-500 mb-2"></i>
              <p class="text-gray-600">Loading data...</p>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="flex items-center justify-center h-64">
            <div class="text-center">
              <i class="fas fa-exclamation-triangle text-2xl text-red-500 mb-2"></i>
              <p class="text-red-600 mb-2">{{ error }}</p>
              <button @click="loadTimelineData" class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600">
                Try Again
              </button>
            </div>
          </div>

          <!-- Content -->
          <div v-else>
                        <!-- Summary Stats -->
                        <div class="hidden grid grid-cols-4 gap-4 mb-6">
                          <div class="bg-blue-50 rounded-lg p-4">
                            <div class="text-sm text-blue-600 mb-1">Total Records</div>
                            <div class="text-2xl font-bold text-blue-800">{{ summary.total_records || 0 }}</div>
                          </div>
                          <div class="bg-green-50 rounded-lg p-4">
                            <div class="text-sm text-green-600 mb-1">Unique Players</div>
                            <div class="text-2xl font-bold text-green-800">{{ summary.unique_players || 0 }}</div>
                          </div>
                          <div class="bg-purple-50 rounded-lg p-4">
                            <div class="text-sm text-purple-600 mb-1">Unique Markets</div>
                            <div class="text-2xl font-bold text-purple-800">{{ summary.unique_markets || 0 }}</div>
                          </div>
                          <div class="bg-gray-50 rounded-lg p-4">
                            <div class="text-sm text-gray-600 mb-1">Date Range</div>
                            <div class="text-sm text-gray-800">
                              {{ formatDate(summary.earliest_record) }} - {{ formatDate(summary.latest_record) }}
                            </div>
                          </div>
                        </div>

                        <!-- Data Density Summary -->
                        <div class="hidden bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 mb-6">
                          <h3 class="text-lg font-semibold text-gray-800 mb-3">Market Data Density</h3>
                          <div class="grid grid-cols-3 gap-4">
                            <div class="text-center">
                              <div class="text-2xl font-bold text-green-600">{{ getDataDensityStats().rich }}</div>
                              <div class="text-sm text-green-600">Rich Markets (10+ points)</div>
                            </div>
                            <div class="text-center">
                              <div class="text-2xl font-bold text-yellow-600">{{ getDataDensityStats().good }}</div>
                              <div class="text-sm text-yellow-600">Good Markets (5-9 points)</div>
                            </div>
                            <div class="text-center">
                              <div class="text-2xl font-bold text-red-600">{{ getDataDensityStats().limited }}</div>
                              <div class="text-sm text-red-600">Limited Markets (<5 points)</div>
                            </div>
                          </div>
                        </div>

          <!-- Grid Header -->
          <div class="bg-gradient-to-r from-slate-800 to-slate-900 rounded-t-xl min-w-[2400px] shadow-lg">
            <div class="flex items-center p-4 text-white text-sm font-semibold">
              <div class="w-40 flex-shrink-0">Player</div>
                <div class="flex-1 grid grid-cols-12 gap-1 min-w-[2400px]">
                <div class="text-center">Points</div>
                <div class="text-center">Rebounds</div>
                <div class="text-center">Assists</div>
                <div class="text-center">3 Points</div>
                <div class="text-center">Turnovers</div>
                <div class="text-center">Blocks</div>
                <div class="text-center">Steals</div>
                <div class="text-center">PRA</div>
                <div class="text-center">PR</div>
                <div class="text-center">PA</div>
                <div class="text-center">RA</div>
                <div class="text-center">BS</div>
              </div>
            </div>
          </div>

          <!-- Grid Body -->
          <div class="bg-white border border-t-0 border-slate-200 rounded-b-xl min-w-[2400px] shadow-lg">
            <div v-if="timelineData.length === 0" class="text-center py-12">
              <i class="fas fa-chart-line text-4xl text-gray-300 mb-4"></i>
              <p class="text-gray-500 text-lg">No timeline data found</p>
              <p class="text-gray-400 text-sm">Try adjusting your filters or check if data exists for this fixture</p>
            </div>
            
            <div v-else>
                          <!-- Legend for Target Time Filter -->
                          <div v-if="showTargetTimeData && filters.targetTime" class="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                            <div class="flex items-center gap-4 text-sm">
                              <div class="flex items-center gap-2">
                                <div class="w-4 h-4 rounded border-2 border-orange-400 bg-orange-50"></div>
                                <span class="text-orange-800 font-medium">Highlighted values were active at your target time</span>
                              </div>
                              <div class="flex items-center gap-2">
                                <div class="w-2 h-2 rounded-full bg-orange-500"></div>
                                <span class="text-orange-700">Orange dot indicates target time values</span>
                              </div>
                            </div>
                          </div>
                          
                          <!-- Group data by player -->
                             <div v-for="player in enhancedGroupedData" :key="player.player_id" 
                                  class="border-b border-slate-100 hover:bg-slate-50 transition-all duration-200 group">
                <div class="flex items-start p-4 bg-gray-50">
                  <!-- Player Info -->
                  <div class="w-40 flex-shrink-0 flex items-center gap-3">
                    <div class="flex items-center gap-2">
                      <!-- <div class="w-6 h-6 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-xs font-semibold text-slate-700 shadow-sm">
                        {{ player.player_id }}
                      </div> -->
                      <!-- <div class="w-8 h-8 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center shadow-sm">
                        <i class="fas fa-user text-blue-600 text-xs"></i>
                      </div> -->
                    </div>
                    <div class="flex-1">
                      <div class="font-semibold text-slate-800 text-sm">{{ player.player_name }}</div>
                      <div class="text-xs text-slate-500 font-medium">{{ player.player_team_name }}</div>
                      <div class="text-xs text-slate-400">{{ formatTime(player.latest_entry?.created_at) }}</div>
                    </div>
                  </div>
                  
                  <!-- Market Grid with Timeline -->
                  <div class="flex-1 grid grid-cols-12 gap-1 min-w-[2400px]">
                    <div v-for="market in ['points', 'total_rebounds', 'assists', 'three_point_field_goal', 'turnovers', 'blocks', 'steals', 'pra', 'pr', 'pa', 'ra', 'bs']" 
                         :key="market" class="relative market-cell">
                      <div class="border border-slate-200 rounded-xl p-2 min-h-[140px] flex flex-col transition-all duration-300 hover:shadow-md hover:border-slate-300 bg-gray-100">
                        <!-- Market Header -->
                        <div 
                          @click="openMarketChart(player.player_id, market)"
                          class="text-xs font-semibold text-slate-600 text-center mb-2 flex items-center justify-center gap-1 cursor-pointer hover:text-blue-600 hover:underline transition-colors"
                          :title="`Click to view ${getMarketDisplayName(market)} chart for ${player.player_name}`"
                        >
                          {{ getMarketDisplayName(market) }}
                          <div v-if="player.markets[market]?.timeline?.some(entry => entry.isTargetTime)" 
                               class="w-2 h-2 rounded-full bg-orange-500" 
                               title="Value active at target time">
                          </div>
                          <div v-if="player.markets[market]?.timeline?.[0]?.is_suspended" 
                               class="w-2 h-2 rounded-full bg-yellow-500" 
                               title="Market is suspended">
                          </div>
                          <div v-if="player.markets[market]?.timeline?.[0]?.is_closed" 
                               class="w-2 h-2 rounded-full bg-red-500" 
                               title="Market is closed">
                          </div>
                        </div>
                        
                        <!-- Market Status Indicator -->
                        <div v-if="player.markets[market]?.timeline?.[0]?.is_suspended || player.markets[market]?.timeline?.[0]?.is_closed" 
                             class="text-center mb-2">
                          <span v-if="player.markets[market]?.timeline?.[0]?.is_suspended" 
                                class="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 font-medium">
                            Suspended
                          </span>
                          <span v-else-if="player.markets[market]?.timeline?.[0]?.is_closed" 
                                class="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800 font-medium">
                            Closed
                          </span>
                        </div>
                        
                                      <!-- Market Timeline Data -->
                                      <div v-if="player.markets[market] && player.markets[market].timeline" class="flex-1">
                                                      <!-- Timestamp -->
                                                      <div class="flex items-center justify-end mb-2">
                                                        
                                                        <div class="text-[10px] text-gray-400 text-right leading-tight">{{ formatDateTimeFull(player.markets[market].timeline[0]?.created_at) }}
                                                        </div>
                                                      </div>
                                        

                                        <!-- All Balanced Lines Display -->
                                        <div v-if="player.markets[market].balancedLines && player.markets[market].balancedLines.length > 0" class="space-y-2">
                                          <div v-for="(balancedLine, index) in player.markets[market].balancedLines" :key="index" 
                                               :class="[
                                                 'border border-slate-200 rounded-lg p-2 transition-all duration-200 shadow-sm relative',
                                                 balancedLine.isTargetTime 
                                                   ? 'bg-gradient-to-br from-orange-100 to-orange-200 border-orange-300 shadow-md' 
                                                   : 'bg-white hover:shadow-md'
                                               ]">
                                           
                                            <!-- Suspended Badge -->
                                             {{ balancedLine.is_suspended }}
                                            <div v-if="balancedLine.is_suspended" 
                                                 class="absolute top-[-8px] left-1/2 transform -translate-x-1/2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md z-10">
                                              Suspended
                                            </div>
                                               
                                            <!-- DateTime Header -->
                                            <div class="text-[10px] text-slate-600 text-center mb-2 font-medium border-b border-slate-200 pb-1">
                                              {{ formatDateTimeFull(balancedLine.created_at) }}
                                            </div>
                                            
                                            <!-- Balance Line Value -->
                                            <div class="text-center mb-2">
                                              <div class="text-sm font-bold text-slate-800">
                                                {{ balancedLine.balance_line?.toFixed(1) || '0.0' }} | {{ balancedLine?.settlement_value }}
                                              </div>
                                            </div>
                                            
                                            <div class="flex items-center">
                                              <!-- Over/Under Odds -->
                                              <div class="flex-1 space-y-2">
                                                <!-- Over Section -->
                                                <div :class="[
                                                  'rounded-lg px-2 py-1.5 flex items-center justify-between text-xs border',
                                                  balancedLine.is_suspended || balancedLine.is_closed
                                                    ? 'bg-gray-100 border-gray-300 opacity-50 grayscale'
                                                    : 'bg-green-50 border-green-200'
                                                ]">
                                                  <div class="flex items-center gap-2 relative">
   
                                                    <div :class="[
                                                      'w-2 h-2 rounded-full',
                                                      balancedLine.is_suspended || balancedLine.is_closed
                                                        ? 'bg-gray-400'
                                                        : 'bg-green-500'
                                                    ]"></div>
                                                    <span :class="[
                                                      'font-semibold',
                                                      balancedLine.is_suspended || balancedLine.is_closed
                                                        ? 'text-gray-600'
                                                        : 'text-green-700'
                                                    ]">O|</span>
                                                    
                                                  </div>
                                                  <div class="flex items-center gap-3">
                                                    <span class="font-bold text-slate-800">{{ balancedLine.balance_line_over_odds?.toFixed(2) || '0.00' }}</span>
                                                    <span class="text-slate-500 font-medium">{{ balancedLine.balance_line_over_probability ? (balancedLine.balance_line_over_probability * 100).toFixed(1) + '%' : '0.0%' }}</span>
                                                  </div>
                                                </div>
                                                <!-- <div class="absolute  right-[-10px] top-[-10px] text-slate-800">
                                                      {{ balancedLine?.settlement_value }}

                                                    </div> -->
                                                <!-- Under Section -->
                                                <div :class="[
                                                  'rounded-lg px-2 py-1.5 flex items-center justify-between text-xs border',
                                                  balancedLine.is_suspended || balancedLine.is_closed
                                                    ? 'bg-gray-100 border-gray-300 opacity-50 grayscale'
                                                    : 'bg-red-50 border-red-200'
                                                ]">
                                                  <div class="flex items-center gap-2">
                                                    <div :class="[
                                                      'w-2 h-2 rounded-full',
                                                      balancedLine.is_suspended || balancedLine.is_closed
                                                        ? 'bg-gray-400'
                                                        : 'bg-red-500'
                                                    ]"></div>
                                                    <span :class="[
                                                      'font-semibold',
                                                      balancedLine.is_suspended || balancedLine.is_closed
                                                        ? 'text-gray-600'
                                                        : 'text-red-700'
                                                    ]">U|</span>
                                                  </div>
                                                  <div class="flex items-center gap-3">
                                                    <span class="font-bold text-slate-800">{{ balancedLine.balance_line_under_odds?.toFixed(2) || '0.00' }}</span>
                                                    <span class="text-slate-500 font-medium">{{ balancedLine.balance_line_under_probability ? (balancedLine.balance_line_under_probability * 100).toFixed(1) + '%' : '0.0%' }}</span>
                                                  </div>
                                                </div>

                                                <div class="text-center text-xs text-slate-500">{{ balancedLine.user_id }}</div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        
                                        <!-- Fallback: Show latest timeline entry if no balanced lines -->
                                        <div v-else class="flex items-center h-full">
                                          <!-- Left Side - Balance Line -->
                                          <div class="flex-shrink-0 w-12 flex items-center justify-center">
                                            <div :class="[
                                              'text-2xl font-bold transition-all duration-200',
                                              player.markets[market].timeline[0]?.isTargetTime 
                                                ? 'text-orange-800 bg-gradient-to-br from-orange-100 to-orange-200 px-3 py-2 rounded-lg shadow-sm border border-orange-200' 
                                                : 'text-slate-800'
                                            ]">
                                              {{ player.markets[market].timeline[0]?.balance_line?.toFixed(1) || '0.0' }}
                                            </div>
                                          </div>
                                          
                                          <!-- Right Side - Over/Under Odds -->
                                          <div class="flex-1 ml-4 space-y-3">
                                            <!-- Over Section -->
                                            <div :class="[
                                              'rounded-lg px-4 py-3 flex items-center justify-between text-xs transition-all duration-200 shadow-sm border',
                                              player.markets[market].timeline[0]?.is_suspended || player.markets[market].timeline[0]?.is_closed
                                                ? 'bg-gray-100 border-gray-300 opacity-50 grayscale'
                                                : player.markets[market].timeline[0]?.isTargetTime 
                                                  ? 'bg-gradient-to-br from-green-100 to-green-200 border-green-300 shadow-md' 
                                                  : 'bg-green-50 border-green-200 hover:shadow-md'
                                            ]">
                                              <div class="flex items-center gap-2">
                                                <div :class="[
                                                  'w-2 h-2 rounded-full',
                                                  player.markets[market].timeline[0]?.is_suspended || player.markets[market].timeline[0]?.is_closed
                                                    ? 'bg-gray-400'
                                                    : 'bg-green-500'
                                                ]"></div>
                                                <span :class="[
                                                  'font-semibold',
                                                  player.markets[market].timeline[0]?.is_suspended || player.markets[market].timeline[0]?.is_closed
                                                    ? 'text-gray-600'
                                                    : 'text-green-700'
                                                ]">O|</span>
                                              </div>
                                              <div class="flex items-center gap-3">
                                                <span class="font-bold text-slate-800">{{ player.markets[market].timeline[0]?.balance_line_over_odds?.toFixed(2) || '0.00' }}</span>
                                                <span class="text-slate-500 font-medium">{{ player.markets[market].timeline[0]?.balance_line_over_probability ? (player.markets[market].timeline[0].balance_line_over_probability * 100).toFixed(1) + '%' : '0.0%' }}</span>
                                              </div>
                                            </div>
                                            
                                            <!-- Under Section -->
                                            <div :class="[
                                              'rounded-lg px-4 py-3 flex items-center justify-between text-xs transition-all duration-200 shadow-sm border',
                                              player.markets[market].timeline[0]?.is_suspended || player.markets[market].timeline[0]?.is_closed
                                                ? 'bg-gray-100 border-gray-300 opacity-50 grayscale'
                                                : player.markets[market].timeline[0]?.isTargetTime 
                                                  ? 'bg-gradient-to-br from-red-100 to-red-200 border-red-300 shadow-md' 
                                                  : 'bg-red-50 border-red-200 hover:shadow-md'
                                            ]">
                                              <div class="flex items-center gap-2">
                                                <div :class="[
                                                  'w-2 h-2 rounded-full',
                                                  player.markets[market].timeline[0]?.is_suspended || player.markets[market].timeline[0]?.is_closed
                                                    ? 'bg-gray-400'
                                                    : 'bg-red-500'
                                                ]"></div>
                                                <span :class="[
                                                  'font-semibold',
                                                  player.markets[market].timeline[0]?.is_suspended || player.markets[market].timeline[0]?.is_closed
                                                    ? 'text-gray-600'
                                                    : 'text-red-700'
                                                ]">U|</span>
                                              </div>
                                              <div class="flex items-center gap-3">
                                                <span class="font-bold text-slate-800">{{ player.markets[market].timeline[0]?.balance_line_under_odds?.toFixed(2) || '0.00' }}</span>
                                                <span class="text-slate-500 font-medium">{{ player.markets[market].timeline[0]?.balance_line_under_probability ? (player.markets[market].timeline[0].balance_line_under_probability * 100).toFixed(1) + '%' : '0.0%' }}</span>
                                              </div>
                                            </div>
                                            
                                          </div>
                                        </div>
                          
                          <!-- Timeline History (Small) -->
                          <div class="hidden mt-2 space-y-1 max-h-12 overflow-y-auto">
                            <div v-for="(entry, index) in player.markets[market].timeline.slice(1, 4)" 
                                 :key="index" 
                                 :class="[
                                   'text-xs flex items-center justify-between p-1 rounded transition-all duration-200',
                                   entry.isTargetTime 
                                     ? 'bg-orange-100 border border-orange-300' 
                                     : 'bg-gray-50'
                                 ]">
                              <div class="flex items-center gap-1">
                                <div class="w-1 h-1 rounded-full bg-gray-400"></div>
                                <span class="text-gray-600">{{ entry.balance_line?.toFixed(1) || '0.0' }}</span>
                              </div>
                              <div class="flex items-center gap-1">
                                <span
                                  class="px-2 py-0.5 rounded-full text-white text-xxs bg-green-500 font-semibold"
                                >{{ entry.balance_line_over_odds?.toFixed(2) || '0.00' }}</span>
                                <span class="text-gray-400 text-xs font-semibold">/</span>
                                <span
                                  class="px-2 py-0.5 rounded-full text-white text-xxs bg-red-500 font-semibold"
                                >{{ entry.balance_line_under_odds?.toFixed(2) || '0.00' }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <!-- No Data State -->
                        <div v-else class="text-center text-slate-400 flex-1 flex flex-col justify-center">
                          <div class="text-lg font-bold text-slate-500">0.0</div>
                          <div class="text-xs font-medium text-slate-400">No Data</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Fixture Details Popup -->
    <div v-if="showFixtureDetails" class="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-2">
      <div class="bg-white rounded-xl shadow-2xl w-[95vw] h-[95vh] flex flex-col overflow-hidden">
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b bg-gray-50">
          <h3 class="text-lg font-semibold text-gray-800">Fixture Details</h3>
          <button 
            @click="showFixtureDetails = false"
            class="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-hidden p-6 flex flex-col">
          <!-- Loading State -->
          <div v-if="loadingFixture" class="flex items-center justify-center h-32">
            <div class="text-center">
              <i class="fas fa-spinner fa-spin text-2xl text-blue-500 mb-2"></i>
              <p class="text-gray-600">Loading fixture details...</p>
            </div>
          </div>

          <!-- Fixture Details -->
          <div v-else-if="fixturesList.length > 0" class="flex flex-col flex-1 min-h-0">
            <!-- Current Fixture Details -->
            <div v-if="fixtureDetails.id" class="bg-blue-50 rounded-lg p-4">
              <h4 class="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                <i class="fas fa-star"></i>
                Current Fixture (ID: {{ fixtureDetails.id }})
              </h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="flex justify-between">
                  <span class="text-gray-600">Game Date:</span>
                  <span class="font-medium">{{ fixtureDetails.gamedate }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">Status:</span>
                  <span class="font-medium">{{ fixtureDetails.published_at ? 'Published' : 'Draft' }}</span>
                </div>
                <div v-if="fixtureDetails.home_team" class="flex justify-between">
                  <span class="text-gray-600">Home Team:</span>
                  <span class="font-medium">{{ fixtureDetails.home_team.name }}</span>
                </div>
                <div v-if="fixtureDetails.away_team" class="flex justify-between">
                  <span class="text-gray-600">Away Team:</span>
                  <span class="font-medium">{{ fixtureDetails.away_team.name }}</span>
                </div>
              </div>
            </div>

            <!-- All Fixtures List -->
            <div class="bg-gray-50 rounded-lg p-4 flex flex-col flex-1 min-h-0 mt-6">
              <h4 class="font-semibold text-gray-800 mb-4">All Fixtures ({{ fixturesList.length }} total)</h4>
              <div class="flex-1 overflow-y-auto min-h-0">
                <!-- Table Header -->
                <div class="bg-slate-800 text-white text-sm font-semibold uppercase tracking-wider">
                  <div class="grid grid-cols-12 gap-3 p-4">
                    <div class="col-span-1 text-center">ID</div>
                    <div class="col-span-2">DATE & TIME</div>
                    <div class="col-span-4">TEAMS</div>
                    <div class="col-span-2">LEAGUE</div>
                    <div class="col-span-1">PUBLISHED BY</div>
                    <div class="col-span-1">PUBLISHED AT</div>
                    <div class="col-span-1">STATUS</div>
                  </div>
                </div>
                
                <!-- Table Body -->
                <div class="bg-white">
                  <div 
                    v-for="fixture in fixturesList" 
                    :key="fixture.id"
                    :class="[
                      'grid grid-cols-12 gap-3 p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer',
                      fixture.id == currentFixtureId 
                        ? 'bg-green-50 border-green-200' 
                        : fixture.id == props.fixtureId
                        ? 'bg-blue-50 border-blue-200'
                        : ''
                    ]"
                    @click="fixture.id !== currentFixtureId ? selectFixture(fixture) : null"
                  >
                    <!-- Fixture ID -->
                    <div class="col-span-1 flex items-center justify-center">
                      <span class="font-semibold text-gray-900 text-sm">#{{ fixture.id }}</span>
                    </div>
                    
                    <!-- Date & Time -->
                    <div class="col-span-2 flex items-center">
                      <span class="text-sm text-gray-700">{{ fixture.gamedate }}</span>
                    </div>
                    
                    <!-- Teams -->
                    <div class="col-span-4 flex items-center">
                      <span class="text-sm text-gray-700">
                        <span v-if="fixture.home_team && fixture.away_team">
                          {{ fixture.home_team.team_name }} vs {{ fixture.away_team.team_name }}
                        </span>
                        <span v-else class="text-gray-400">No teams data</span>
                      </span>
                    </div>
                    
                    <!-- League -->
                    <div class="col-span-2 flex items-center">
                      <div v-if="fixture.league" class="text-sm">
                        <div class="text-gray-900 font-medium">{{ fixture.league.league_name }}</div>
                        <div class="text-xs text-gray-500">{{ fixture.league.slug }}</div>
                      </div>
                      <span v-else class="text-sm text-gray-400">No league</span>
                    </div>
                    
                    <!-- Published By -->
                    <div class="col-span-1 flex items-center">
                      <span class="text-sm text-gray-700 truncate">
                        {{ fixture.user_data?.name || 'Unknown' }}
                      </span>
                    </div>
                    
                    <!-- Published At -->
                    <div class="col-span-1 flex items-center">
                      <span class="text-xs text-gray-600">
                        {{ fixture.published_at ? formatDateTime(fixture.published_at) : 'Not published' }}
                      </span>
                    </div>
                    
                    <!-- Status -->
                    <div class="col-span-1 flex items-center justify-end">
                      <div class="flex items-center gap-2">
                        <span :class="[
                          'text-xs px-2 py-1 rounded-full font-medium',
                          fixture.published_at ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        ]">
                          {{ fixture.published_at ? 'Published' : 'Draft' }}
                        </span>
                        <span v-if="fixture.id == currentFixtureId" class="text-xs bg-green-200 text-green-800 px-2 py-1 rounded-full font-medium">
                          Viewing
                        </span>
                        <span v-else-if="fixture.id == props.fixtureId" class="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded-full font-medium">
                          Original
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="!loadingFixture" class="text-center py-12">
            <i class="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
            <p class="text-red-600 mb-2">Failed to load fixture details</p>
            <button @click="loadFixtureDetails" class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600">
              Try Again
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Market Chart Popup -->
    <div v-if="showMarketChart" class="fixed inset-0 bg-black bg-opacity-50 z-[80] flex items-center justify-center p-4">
      <div class="bg-white rounded-xl shadow-2xl w-[90vw] h-[80vh] flex flex-col overflow-hidden">
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b bg-gray-50">
          <div>
            <h3 class="text-lg font-semibold text-gray-800">
              {{ selectedMarketData?.player?.player_name }} - {{ getMarketDisplayName(selectedMarketType) }} Chart
            </h3>
            <p class="text-sm text-gray-600">Value changes over time (newest on right)</p>
          </div>
          <div class="flex items-center gap-3">
            <!-- Chart View Toggle -->
            <div class="flex items-center bg-gray-200 rounded-lg p-1">
              <button
                @click="chartViewType = 'bars'"
                :class="[
                  'px-3 py-1 rounded-md text-sm font-medium transition-colors',
                  chartViewType === 'bars' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                ]"
              >
                <i class="fas fa-chart-bar mr-1"></i>
                Bars
              </button>
              <button
                @click="chartViewType = 'lines'"
                :class="[
                  'px-3 py-1 rounded-md text-sm font-medium transition-colors',
                  chartViewType === 'lines' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                ]"
              >
                <i class="fas fa-chart-line mr-1"></i>
                Lines
              </button>
            </div>
            <!-- Close Button -->
            <button 
              @click="showMarketChart = false"
              class="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Chart Content -->
        <div class="flex-1 p-6 overflow-auto">
          <div v-if="selectedMarketData" class="space-y-6">
            <!-- Chart Container -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h4 class="font-semibold text-gray-800 mb-4">Timeline Chart</h4>
              <div class="h-96 bg-white rounded border p-4">
                <canvas ref="marketChartCanvas"></canvas>
              </div>
            </div>

            <!-- Data Table -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h4 class="font-semibold text-gray-800 mb-4">Detailed Timeline Data</h4>
              <div class="overflow-x-auto">
                <table class="min-w-full text-sm">
                  <thead class="bg-gray-200">
                    <tr>
                      <th class="px-3 py-2 text-left">Time</th>
                      <th class="px-3 py-2 text-left">Balance Line</th>
                      <th class="px-3 py-2 text-left">Over Odds</th>
                      <th class="px-3 py-2 text-left">Under Odds</th>
                      <th class="px-3 py-2 text-left">Over Prob</th>
                      <th class="px-3 py-2 text-left">Under Prob</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr 
                      v-for="(entry, index) in selectedMarketData.timeline.slice().reverse()" 
                      :key="index"
                      :class="[
                        'border-b border-gray-100',
                        entry.isTargetTime ? 'bg-orange-50' : ''
                      ]"
                    >
                      <td class="px-3 py-2">{{ formatDateTime(entry.created_at) }} - {{ entry.user_id }}</td>
                      <td class="px-3 py-2 font-medium">{{ entry.balance_line?.toFixed(1) || '0.0' }}</td>
                      <td class="px-3 py-2 text-green-600">{{ entry.balance_line_over_odds?.toFixed(2) || '0.00' }}</td>
                      <td class="px-3 py-2 text-red-600">{{ entry.balance_line_under_odds?.toFixed(2) || '0.00' }}</td>
                      <td class="px-3 py-2">{{ entry.balance_line_over_probability ? (entry.balance_line_over_probability * 100).toFixed(1) + '%' : '0.0%' }}</td>
                      <td class="px-3 py-2">{{ entry.balance_line_under_probability ? (entry.balance_line_under_probability * 100).toFixed(1) + '%' : '0.0%' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Global Chart Popup -->
    <div v-if="showGlobalChart" class="fixed inset-0 bg-black bg-opacity-50 z-[90] flex items-center justify-center p-2">
      <div class="bg-white rounded-xl shadow-2xl w-[95vw] h-[95vh] flex overflow-hidden">
        <!-- Left Sidebar - Player Selection -->
        <div v-if="showPlayerSelection" class="w-80 bg-gray-50 border-r border-gray-200 flex flex-col">
          <!-- Player Selection Header -->
          <div class="p-4 border-b border-gray-200 bg-white">
            <div class="flex items-center justify-between mb-3">
              <h4 class="font-semibold text-gray-800">Select Players</h4>
              <button
                @click="showPlayerSelection = false"
                class="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <i class="fas fa-times text-sm"></i>
              </button>
            </div>
            <div class="flex gap-2">
              <button
                @click="selectAllPlayers"
                class="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors"
              >
                Select All
              </button>
              <button
                @click="clearAllPlayers"
                class="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
          
          <!-- Player List -->
          <div class="flex-1 overflow-y-auto p-4">
            <div class="space-y-2">
              <div
                v-for="player in enhancedGroupedData"
                :key="player.player_id"
                @click="togglePlayerSelection(player.player_id)"
                :class="[
                  'p-3 rounded-lg border cursor-pointer transition-all duration-200',
                  selectedPlayers.has(player.player_id)
                    ? 'bg-blue-50 border-blue-300 shadow-sm'
                    : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                ]"
              >
                <div class="flex items-center justify-between">
                  <div class="flex-1">
                    <div class="font-medium text-gray-900 text-sm">
                      {{ player.player_name }}
                    </div>
                    <div class="text-xs text-gray-500 mt-1">
                      {{ Object.keys(player.markets).length }} markets
                    </div>
                  </div>
                  <div class="ml-3">
                    <div
                      :class="[
                        'w-5 h-5 rounded border-2 flex items-center justify-center',
                        selectedPlayers.has(player.player_id)
                          ? 'bg-blue-500 border-blue-500'
                          : 'border-gray-300'
                      ]"
                    >
                      <i
                        v-if="selectedPlayers.has(player.player_id)"
                        class="fas fa-check text-white text-xs"
                      ></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Main Content Area -->
        <div class="flex-1 flex flex-col">
          <!-- Header -->
          <div class="flex items-center justify-between p-4 border-b bg-gray-50">
            <div class="flex items-center gap-4">
              <button
                v-if="!showPlayerSelection"
                @click="showPlayerSelection = true"
                class="px-3 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
              >
                <i class="fas fa-users mr-2"></i>
                Select Players
              </button>
              <div>
                <h3 class="text-lg font-semibold text-gray-800">
                  Global Timeline Chart - All Markets
                </h3>
                <p class="text-sm text-gray-600">
                  {{ selectedPlayers.size }} players selected • All markets as separate lines
                </p>
              </div>
            </div>
            <div class="flex items-center gap-3">
              <!-- Close Button -->
              <button 
                @click="showGlobalChart = false"
                class="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>

          <!-- Chart Content -->
          <div class="flex-1 p-6 overflow-auto">
            <div class="space-y-6">
              <!-- Chart Container -->
              <div class="bg-gray-50 rounded-lg p-4">
                <h4 class="font-semibold text-gray-800 mb-4">
                  All Markets Timeline - Selected Players
                </h4>
                <div class="h-96 bg-white rounded border p-4">
                  <canvas ref="globalChartCanvas"></canvas>
                </div>
              </div>

              <!-- Legend Info -->
              <div class="bg-gray-50 rounded-lg p-4">
                <h4 class="font-semibold text-gray-800 mb-3">Chart Legend</h4>
                <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
                  <div
                    v-for="market in ['points', 'total_rebounds', 'assists', 'three_point_field_goal', 'turnovers', 'blocks', 'steals', 'pra', 'pr', 'pa', 'ra', 'bs']"
                    :key="market"
                    class="flex items-center gap-2"
                  >
                    <div
                      class="w-4 h-0.5 border-t-2"
                      :style="{ borderColor: '#3b82f6', borderDash: getMarketDashPattern(market).join(' ') }"
                    ></div>
                    <span class="text-gray-600">{{ getMarketDisplayName(market) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick, onUnmounted } from 'vue'
import axios from 'axios'
import { Chart, registerables } from 'chart.js'

// Register Chart.js components
Chart.register(...registerables)

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  fixtureId: {
    type: [String, Number],
    required: true
  }
})

const emit = defineEmits(['close', 'fixture-selected'])

// Reactive data
const loading = ref(false)
const error = ref(null)
const timelineData = ref([])
const summary = ref({})
const playersList = ref({})
const fixtureData = ref({})
const showFixtureDetails = ref(false)
const fixtureDetails = ref({})
const fixturesList = ref([])
const loadingFixture = ref(false)
const currentFixtureId = ref(props.fixtureId)

// Time navigation
const currentTimestampIndex = ref(0)
const availableTimestamps = ref([])
const currentTimestamp = ref(null)
const showTargetTimeData = ref(false)

// Filters
const filters = ref({
  marketType: '',
  playerId: '',
  startDate: '',
  endDate: '',
  targetTime: '',
  isBalanced: '1'  // Always fetch only balanced lines (is_balanced = 1)
})

// Toggle for showing only different values
const showOnlyDifferentValues = ref(true)

// Toggle for sidepanel visibility
const showSidepanel = ref(true)

// Fixture filters
const fixtureFilters = ref({
  status: 'all', // 'all', 'published', 'draft'
  search: '',
  league: 'all'
})

// Toggle for showing logs from published date
const showLogsFromPublished = ref(true)

// Market chart popup state
const showMarketChart = ref(false)
const selectedMarketData = ref(null)
const selectedPlayerId = ref(null)
const selectedMarketType = ref(null)
const chartViewType = ref('lines') // 'bars' or 'lines'
const marketChart = ref(null)
const marketChartCanvas = ref(null)

// Global chart popup state
const showGlobalChart = ref(false)
const globalChartViewType = ref('lines') // 'bars' or 'lines'
const selectedGlobalMarket = ref('points')
const globalChart = ref(null)
const globalChartCanvas = ref(null)
const selectedPlayers = ref(new Set())
const showPlayerSelection = ref(true)

// Computed properties
const uniquePlayers = computed(() => {
  // Use the players_list from API response if available, otherwise fallback to timeline data
  if (Object.keys(playersList.value).length > 0) {
    return Object.entries(playersList.value).map(([player_id, player_name]) => ({
      player_id: parseInt(player_id),
      player_name: player_name
    }))
  }
  
  // Fallback to extracting from timeline data
  const players = new Map()
  timelineData.value.forEach(entry => {
    if (entry.player_id && entry.player_name) {
      players.set(entry.player_id, {
        player_id: entry.player_id,
        player_name: entry.player_name
      })
    }
  })
  return Array.from(players.values())
})

// Filtered fixtures list
const filteredFixturesList = computed(() => {
  let filtered = [...fixturesList.value]
  
  // Filter by status
  if (fixtureFilters.value.status !== 'all') {
    if (fixtureFilters.value.status === 'published') {
      filtered = filtered.filter(f => f.published_at)
    } else if (fixtureFilters.value.status === 'draft') {
      filtered = filtered.filter(f => !f.published_at)
    }
  }
  
  // Filter by league
  if (fixtureFilters.value.league !== 'all') {
    filtered = filtered.filter(f => {
      if (!f.league) return false
      return f.league.slug === fixtureFilters.value.league || 
             f.league.league_name === fixtureFilters.value.league
    })
  }
  
  // Filter by search (fixture ID, team names)
  if (fixtureFilters.value.search) {
    const searchLower = fixtureFilters.value.search.toLowerCase()
    filtered = filtered.filter(f => {
      // Search by fixture ID
      if (f.id.toString().includes(searchLower)) return true
      
      // Search by team names
      if (f.home_team?.team_name?.toLowerCase().includes(searchLower)) return true
      if (f.away_team?.team_name?.toLowerCase().includes(searchLower)) return true
      
      // Search by league name
      if (f.league?.league_name?.toLowerCase().includes(searchLower)) return true
      if (f.league?.slug?.toLowerCase().includes(searchLower)) return true
      
      return false
    })
  }
  
  return filtered
})

// Get unique leagues for filter dropdown
const uniqueLeagues = computed(() => {
  const leagues = new Map()
  fixturesList.value.forEach(fixture => {
    if (fixture.league) {
      leagues.set(fixture.league.slug, fixture.league.league_name)
    }
  })
  return Array.from(leagues.entries()).map(([slug, name]) => ({ slug, name }))
})

                        // Group data by player and market with timeline
                        const groupedData = computed(() => {
                          const grouped = new Map()
                          
                          // First, group by player
                          timelineData.value.forEach(entry => {
                            const playerId = entry.player_id
                            
                            if (!grouped.has(playerId)) {
                              // Use players_list for player name if available, otherwise fallback to entry data
                              const playerName = playersList.value[playerId] || entry.player_name
                              
                              grouped.set(playerId, {
                                player_id: playerId,
                                player_name: playerName,
                                player_team_name: entry.player_team_name,
                                markets: {},
                                latest_entry: entry
                              })
                            }
                            
                            // Store the latest entry for this player
                            if (new Date(entry.created_at) > new Date(grouped.get(playerId).latest_entry.created_at)) {
                              grouped.get(playerId).latest_entry = entry
                            }
                          })
                          
                          // Then, group by market for each player and create timeline
                          timelineData.value.forEach(entry => {
                            const playerId = entry.player_id
                            const marketType = entry.market_type
                            
                            if (!grouped.get(playerId).markets[marketType]) {
                              grouped.get(playerId).markets[marketType] = {
                                timeline: [],
                                balancedLines: []
                              }
                            }
                            
                            // Add to timeline (all entries)
                            grouped.get(playerId).markets[marketType].timeline.push(entry)
                            
                            // Separate balanced lines for special display
                            if (entry.is_balanced === 1) {
                              grouped.get(playerId).markets[marketType].balancedLines.push(entry)
                            }
                          })
                          
                          // Sort timeline by created_at (newest first)
                          grouped.forEach(player => {
                            Object.keys(player.markets).forEach(marketType => {
                              if (player.markets[marketType].timeline) {
                                player.markets[marketType].timeline.sort((a, b) => 
                                  new Date(b.created_at) - new Date(a.created_at)
                                )
                              }
                              if (player.markets[marketType].balancedLines) {
                                player.markets[marketType].balancedLines.sort((a, b) => 
                                  new Date(b.created_at) - new Date(a.created_at)
                                )
                              }
                            })
                          })
                          
  return Array.from(grouped.values())
})

// Filter data to show only different values when toggle is active
const filteredGroupedData = computed(() => {
  if (!showOnlyDifferentValues.value) {
    return groupedData.value
  }
  
  return groupedData.value.map(player => {
    const filteredPlayer = {
      ...player,
      markets: {}
    }
    
    Object.keys(player.markets).forEach(marketType => {
      const marketData = player.markets[marketType]
      const timeline = marketData.timeline || []
      const balancedLines = marketData.balancedLines || []
      
      // Filter timeline to show only entries with different values (balance_line, over odds, or under odds)
      // Sort timeline by created_at (oldest first) for proper sequential filtering
      const sortedTimeline = [...timeline].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      
      const filteredTimeline = sortedTimeline.filter((entry, index) => {
        if (index === 0) return true // Always show the first entry
        
        const prevEntry = sortedTimeline[index - 1]
        
        // Check if balance_line changed
        const currentBalanceLine = entry.balance_line?.toFixed(1) || '0.0'
        const prevBalanceLine = prevEntry.balance_line?.toFixed(1) || '0.0'
        const balanceLineChanged = currentBalanceLine !== prevBalanceLine
        
        // Check if over odds changed
        const currentOverOdds = entry.balance_line_over_odds?.toFixed(2) || '0.00'
        const prevOverOdds = prevEntry.balance_line_over_odds?.toFixed(2) || '0.00'
        const overOddsChanged = currentOverOdds !== prevOverOdds
        
        // Check if under odds changed
        const currentUnderOdds = entry.balance_line_under_odds?.toFixed(2) || '0.00'
        const prevUnderOdds = prevEntry.balance_line_under_odds?.toFixed(2) || '0.00'
        const underOddsChanged = currentUnderOdds !== prevUnderOdds
        
        // Show entry if any value changed
        return balanceLineChanged || overOddsChanged || underOddsChanged
      })
      
      // Filter balanced lines to show only entries with different values (balance_line, over odds, or under odds)
      // Sort balanced lines by created_at (oldest first) for proper sequential filtering
      const sortedBalancedLines = [...balancedLines].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      
      const filteredBalancedLines = sortedBalancedLines.filter((entry, index) => {
        if (index === 0) return true // Always show the first entry
        
        const prevEntry = sortedBalancedLines[index - 1]
        
        // Check if balance_line changed
        const currentBalanceLine = entry.balance_line?.toFixed(1) || '0.0'
        const prevBalanceLine = prevEntry.balance_line?.toFixed(1) || '0.0'
        const balanceLineChanged = currentBalanceLine !== prevBalanceLine
        
        // Check if over odds changed
        const currentOverOdds = entry.balance_line_over_odds?.toFixed(2) || '0.00'
        const prevOverOdds = prevEntry.balance_line_over_odds?.toFixed(2) || '0.00'
        const overOddsChanged = currentOverOdds !== prevOverOdds
        
        // Check if under odds changed
        const currentUnderOdds = entry.balance_line_under_odds?.toFixed(2) || '0.00'
        const prevUnderOdds = prevEntry.balance_line_under_odds?.toFixed(2) || '0.00'
        const underOddsChanged = currentUnderOdds !== prevUnderOdds
        
        // Show entry if any value changed
        return balanceLineChanged || overOddsChanged || underOddsChanged
      })
      
      // Only include market if it has filtered data
      if (filteredTimeline.length > 0 || filteredBalancedLines.length > 0) {
        // Sort filtered data back to newest first for display
        const displayTimeline = [...filteredTimeline].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        const displayBalancedLines = [...filteredBalancedLines].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        
        filteredPlayer.markets[marketType] = {
          ...marketData,
          timeline: displayTimeline,
          balancedLines: displayBalancedLines
        }
      }
    })
    
    return filteredPlayer
  }).filter(player => Object.keys(player.markets).length > 0) // Only show players with markets
})

// Add target time highlighting to existing data
const enhancedGroupedData = computed(() => {
  const dataToEnhance = filteredGroupedData.value
  
  if (!showTargetTimeData.value || !filters.value.targetTime || availableTimestamps.value.length === 0) {
    return dataToEnhance
  }
  
  const targetDate = new Date(filters.value.targetTime)
  
  return dataToEnhance.map(player => {
    const enhancedPlayer = {
      ...player,
      markets: {}
    }
    
    Object.keys(player.markets).forEach(marketType => {
      const marketData = { ...player.markets[marketType] }
      const timeline = marketData.timeline
      
      if (timeline && timeline.length > 0) {
        // Find the closest past entry to target time
        const validEntries = timeline.filter(entry => {
          const entryDate = new Date(entry.created_at)
          return !isNaN(entryDate.getTime()) && entryDate <= targetDate
        })
        
        if (validEntries.length > 0) {
          // Find closest past entry
          const closestEntry = validEntries.reduce((closest, current) => {
            const closestDate = new Date(closest.created_at)
            const currentDate = new Date(current.created_at)
            const targetDiff = Math.abs(targetDate - closestDate)
            const currentDiff = Math.abs(targetDate - currentDate)
            
            return currentDiff < targetDiff ? current : closest
          })
          
          // Mark the closest entry as target time
          marketData.timeline = timeline.map(entry => ({
            ...entry,
            isTargetTime: entry.created_at === closestEntry.created_at
          }))
          
          marketData.balancedLines = marketData.balancedLines?.map(entry => ({
            ...entry,
            isTargetTime: entry.created_at === closestEntry.created_at
          })) || []
        }
      }
      
      enhancedPlayer.markets[marketType] = marketData
    })
    
    return enhancedPlayer
  })
})

// Get data for current timestamp
const currentTimestampData = computed(() => {
  if (!currentTimestamp.value || availableTimestamps.value.length === 0) {
    return groupedData.value
  }
  
  const grouped = new Map()
  
  // Group by player and show data for current timestamp
  groupedData.value.forEach(player => {
    const playerData = {
      ...player,
      markets: {}
    }
    
    Object.keys(player.markets).forEach(marketType => {
      const timeline = player.markets[marketType].timeline
      if (timeline && timeline.length > 0) {
        // Find entry closest to current timestamp
        const currentEntry = timeline.find(entry => 
          entry.created_at === currentTimestamp.value
        ) || timeline[0] // Fallback to first entry
        
        playerData.markets[marketType] = {
          timeline: [currentEntry]
        }
      }
    })
    
    grouped.set(player.player_id, playerData)
  })
  
  return Array.from(grouped.values())
})

// Methods
const findClosestPastTimestamp = (targetTime, timestamps) => {
  if (!targetTime || timestamps.length === 0) return null
  
  const targetDate = new Date(targetTime)
  const validTimestamps = timestamps.filter(ts => {
    const tsDate = new Date(ts)
    return !isNaN(tsDate.getTime()) && tsDate <= targetDate
  })
  
  if (validTimestamps.length === 0) return null
  
  // Find the closest past timestamp
  const closest = validTimestamps.reduce((closest, current) => {
    const closestDate = new Date(closest)
    const currentDate = new Date(current)
    const targetDiff = Math.abs(targetDate - closestDate)
    const currentDiff = Math.abs(targetDate - currentDate)
    
    return currentDiff < targetDiff ? current : closest
  })
  
  return closest
}

const applyTargetTimeFilter = () => {
  showTargetTimeData.value = !showTargetTimeData.value
}

const loadTimelineData = async () => {
  if (!props.fixtureId) return
  
  loading.value = true
  error.value = null
  
  try {
    const params = new URLSearchParams()
    
    if (filters.value.marketType) params.append('market_type', filters.value.marketType)
    if (filters.value.playerId) params.append('player_id', filters.value.playerId)
    if (filters.value.startDate) params.append('start_date', filters.value.startDate)
    if (filters.value.endDate) params.append('end_date', filters.value.endDate)
    if (filters.value.isBalanced !== '') params.append('is_balanced', filters.value.isBalanced)
    
    const response = await axios.get(`/api/timeline-clickhouse/${props.fixtureId}?${params.toString()}`)

    if (response.data.success) {
      timelineData.value = response.data.data || []
      summary.value = response.data.meta || {}
      playersList.value = response.data.meta?.players_list || {}
      fixtureData.value = response.data.meta?.fixture_data || {}
      
      // Extract unique timestamps and sort them (newest first)
      const timestamps = [...new Set(timelineData.value.map(entry => entry.created_at))]
        .sort((a, b) => new Date(b) - new Date(a))
      
      availableTimestamps.value = timestamps
      
      // If target time is specified, find closest past timestamp
      if (filters.value.targetTime) {
        const closestTimestamp = findClosestPastTimestamp(filters.value.targetTime, timestamps)
        if (closestTimestamp) {
          const closestIndex = timestamps.findIndex(ts => ts === closestTimestamp)
          currentTimestampIndex.value = closestIndex
          currentTimestamp.value = closestTimestamp
        } else {
          // If no past timestamp found, use latest
          currentTimestampIndex.value = 0
          currentTimestamp.value = timestamps[0]
        }
      } else {
        // Default behavior - start with latest
        currentTimestampIndex.value = 0
        currentTimestamp.value = timestamps[0]
      }
    } else {
      throw new Error(response.data.message || 'Failed to load timeline data')
    }
  } catch (err) {
    console.error('Error loading timeline data:', err)
    error.value = err.response?.data?.message || err.message || 'Failed to load timeline data'
  } finally {
    loading.value = false
  }
}

// Toggle logs from published date
const toggleLogsFromPublished = async () => {
  if (!showLogsFromPublished.value) {
    // Turning ON: Show logs from published date
    if (!fixtureData.value.published_at) {
      alert('No published_at date available for this fixture. Showing all logs instead.')
      showLogsFromPublished.value = true // Still toggle the button state
      return
    }
    
    // Parse the published_at date with proper error handling
    let publishedDate
    try {
      // Handle different date formats
      const publishedAtString = fixtureData.value.published_at
      
      // Check for European format: "DD.MM.YYYY HH:mm:ss"
      if (publishedAtString.includes('.') && publishedAtString.includes(' ')) {
        // Format: "22.10.2025 14:44:54" - European format
        const [datePart, timePart] = publishedAtString.split(' ')
        const [day, month, year] = datePart.split('.')
        const [hours, minutes, seconds] = timePart.split(':')
        
        // Create date in ISO format: YYYY-MM-DDTHH:mm:ss
        const isoString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hours}:${minutes}:${seconds}`
        publishedDate = new Date(isoString)
      } else if (publishedAtString.includes(' ')) {
        // Format: "2025-10-21 19:21:21" - ISO format with space
        publishedDate = new Date(publishedAtString.replace(' ', 'T'))
      } else {
        // Try direct parsing
        publishedDate = new Date(publishedAtString)
      }
      
      // Check if the date is valid
      if (isNaN(publishedDate.getTime())) {
        throw new Error('Invalid date format')
      }
      
      const startDate = publishedDate.toISOString().split('T')[0] // Format as YYYY-MM-DD
      
      filters.value.startDate = startDate
      filters.value.endDate = '' // Clear end date to show all data from published date onwards
      showLogsFromPublished.value = true
      
    } catch (error) {
      console.error('Error parsing published_at date:', error)
      alert(`Invalid published_at date format: ${fixtureData.value.published_at}. Showing all logs instead.`)
      showLogsFromPublished.value = true // Still toggle the button state
      return
    }
  } else {
    // Turning OFF: Show all logs
    filters.value.startDate = ''
    filters.value.endDate = ''
    showLogsFromPublished.value = false
  }
  
  // Reload data with the new filter
  await loadTimelineData()
}

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A'
  
  // Handle the specific format "2025-10-21 19:21:21"
  let date
  if (typeof dateString === 'string' && dateString.includes(' ')) {
    // Convert "2025-10-21 19:21:21" to ISO format for proper parsing
    date = new Date(dateString.replace(' ', 'T'))
  } else {
    date = new Date(dateString)
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return dateString // Return original string if parsing fails
  }
  
  return date.toLocaleString('et-EE', {
    timeZone: 'Europe/Tallinn',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  
  // Handle the specific format "2025-10-21 19:21:21"
  let date
  if (typeof dateString === 'string' && dateString.includes(' ')) {
    // Convert "2025-10-21 19:21:21" to ISO format for proper parsing
    date = new Date(dateString.replace(' ', 'T'))
  } else {
    date = new Date(dateString)
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return dateString // Return original string if parsing fails
  }
  
  return date.toLocaleString('et-EE', {
    timeZone: 'Europe/Tallinn',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })
}

const formatTime = (dateString) => {
  if (!dateString) return 'N/A'
  
  // Handle the specific format "2025-10-21 19:21:21"
  let date
  if (typeof dateString === 'string' && dateString.includes(' ')) {
    // Convert "2025-10-21 19:21:21" to ISO format for proper parsing
    date = new Date(dateString.replace(' ', 'T'))
  } else {
    date = new Date(dateString)
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return dateString // Return original string if parsing fails
  }
  
  return date.toLocaleTimeString('et-EE', {
    timeZone: 'Europe/Tallinn',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })
}

const formatDateTimeFull = (dateString) => {
  if (!dateString) return 'N/A'
  
  // Handle the specific format "2025-10-21 19:21:21"
  let date
  if (typeof dateString === 'string' && dateString.includes(' ')) {
    // Convert "2025-10-21 19:21:21" to ISO format for proper parsing
    date = new Date(dateString.replace(' ', 'T'))
  } else {
    date = new Date(dateString)
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return dateString // Return original string if parsing fails
  }
  
  return date.toLocaleString('et-EE', {
    timeZone: 'Europe/Tallinn',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })
}

// Open market chart popup
const openMarketChart = (playerId, marketType) => {
  selectedPlayerId.value = playerId
  selectedMarketType.value = marketType
  
  // Get market data for the selected player and market
  const playerData = enhancedGroupedData.value.find(p => p.player_id === playerId)
  if (playerData && playerData.markets[marketType]) {
    selectedMarketData.value = {
      player: playerData,
      market: playerData.markets[marketType],
      timeline: playerData.markets[marketType].timeline || [],
      balancedLines: playerData.markets[marketType].balancedLines || []
    }
    showMarketChart.value = true
    
    // Create chart after DOM update with delay to ensure canvas is ready
    nextTick(() => {
      setTimeout(() => {
        createMarketChart()
      }, 100)
    })
  }
}

// Toggle chart view type
const toggleChartView = () => {
  chartViewType.value = chartViewType.value === 'bars' ? 'lines' : 'bars'
  nextTick(() => {
    createMarketChart()
  })
}

// Create market chart using Chart.js
const createMarketChart = () => {
  if (!selectedMarketData.value || !marketChartCanvas.value) return
  
  // Ensure canvas is properly mounted
  if (!marketChartCanvas.value.getContext) return
  
  // Destroy existing chart
  if (marketChart.value) {
    marketChart.value.destroy()
    marketChart.value = null
  }
  
  const timeline = selectedMarketData.value.timeline.slice().reverse() // Newest on right
  
  const chartData = {
    labels: timeline.map(entry => formatTime(entry.created_at)),
    datasets: [
      {
        label: 'Balance Line',
        data: timeline.map(entry => entry.balance_line || 0),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 3,
        fill: false,
        stepped: true,
        tension: 0
      },
      {
        label: 'Over Odds',
        data: timeline.map(entry => entry.balance_line_over_odds || 0),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        borderWidth: 2,
        fill: false,
        stepped: true,
        tension: 0
      },
      {
        label: 'Under Odds',
        data: timeline.map(entry => entry.balance_line_under_odds || 0),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        borderWidth: 2,
        fill: false,
        stepped: true,
        tension: 0
      }
    ]
  }
  
  const config = {
    type: chartViewType.value === 'bars' ? 'bar' : 'line',
    data: chartData,
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: `${selectedMarketData.value.player.player_name} - ${getMarketDisplayName(selectedMarketType.value)} Timeline`
        },
        legend: {
          display: true,
          position: 'top'
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: function(context) {
              const index = context[0].dataIndex
              return formatDateTime(timeline[index].created_at)
            },
            label: function(context) {
              const value = context.parsed.y
              const label = context.dataset.label
              return `${label}: ${value.toFixed(2)}`
            }
          }
        }
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: 'Time (newest on right)'
          }
        },
        y: {
          display: true,
          title: {
            display: true,
            text: 'Value'
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      }
    }
  }
  
  try {
    marketChart.value = new Chart(marketChartCanvas.value, config)
  } catch (error) {
    console.error('Error creating market chart:', error)
  }
}

// Open global chart popup
const openGlobalChart = () => {
  showGlobalChart.value = true
  // Select first 5 players by default
  selectedPlayers.value = new Set(enhancedGroupedData.value.slice(0, 5).map(p => p.player_id))
  nextTick(() => {
    setTimeout(() => {
      createGlobalChart()
    }, 100)
  })
}

// Toggle player selection
const togglePlayerSelection = (playerId) => {
  if (selectedPlayers.value.has(playerId)) {
    selectedPlayers.value.delete(playerId)
  } else {
    selectedPlayers.value.add(playerId)
  }
  nextTick(() => {
    setTimeout(() => {
      createGlobalChart()
    }, 50)
  })
}

// Select all players
const selectAllPlayers = () => {
  selectedPlayers.value = new Set(enhancedGroupedData.value.map(p => p.player_id))
  nextTick(() => {
    setTimeout(() => {
      createGlobalChart()
    }, 50)
  })
}

// Clear all player selections
const clearAllPlayers = () => {
  selectedPlayers.value.clear()
  nextTick(() => {
    setTimeout(() => {
      createGlobalChart()
    }, 50)
  })
}

// Toggle global chart view type
const toggleGlobalChartView = () => {
  globalChartViewType.value = globalChartViewType.value === 'bars' ? 'lines' : 'bars'
  nextTick(() => {
    createGlobalChart()
  })
}

// Create global chart using Chart.js
const createGlobalChart = () => {
  if (!globalChartCanvas.value) return
  
  // Ensure canvas is properly mounted
  if (!globalChartCanvas.value.getContext) return
  
  // Destroy existing chart
  if (globalChart.value) {
    globalChart.value.destroy()
    globalChart.value = null
  }
  
  // Get selected players
  const selectedPlayersList = enhancedGroupedData.value.filter(player => 
    selectedPlayers.value.has(player.player_id)
  )
  
  if (selectedPlayersList.length === 0) return
  
  // Get all unique timestamps across all markets for selected players
  const allTimestamps = new Set()
  const allMarkets = ['points', 'total_rebounds', 'assists', 'three_point_field_goal', 'turnovers', 'blocks', 'steals', 'pra', 'pr', 'pa', 'ra', 'bs']
  
  selectedPlayersList.forEach(player => {
    allMarkets.forEach(market => {
      if (player.markets[market]?.timeline?.length > 0) {
        player.markets[market].timeline.forEach(entry => {
          allTimestamps.add(entry.created_at)
        })
      }
    })
  })
  
  const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => new Date(a) - new Date(b))
  
  const datasets = []
  let colorIndex = 0
  
  // Create datasets for each selected player and each market
  selectedPlayersList.forEach(player => {
    allMarkets.forEach(market => {
      if (player.markets[market]?.timeline?.length > 0) {
        const marketData = new Array(sortedTimestamps.length).fill(null)
        
        player.markets[market].timeline.forEach(entry => {
          const timestampIndex = sortedTimestamps.indexOf(entry.created_at)
          if (timestampIndex !== -1) {
            marketData[timestampIndex] = entry.balance_line || 0
          }
        })
        
        datasets.push({
          label: `${player.player_name} - ${getMarketDisplayName(market)}`,
          data: marketData,
          borderColor: getPlayerColor(colorIndex % 15),
          backgroundColor: getPlayerColor(colorIndex % 15) + '20',
          borderWidth: 2,
          fill: false,
          stepped: true,
          tension: 0,
          pointRadius: 2,
          pointHoverRadius: 4,
          borderDash: getMarketDashPattern(market)
        })
        
        colorIndex++
      }
    })
  })
  
  const chartData = {
    labels: sortedTimestamps.map(ts => formatTime(ts)),
    datasets: datasets
  }
  
  const config = {
    type: 'line',
    data: chartData,
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: `All Markets Timeline - Selected Players`
        },
        legend: {
          display: true,
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 15,
            boxWidth: 12
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: function(context) {
              const index = context[0].dataIndex
              return formatDateTime(sortedTimestamps[index])
            },
            label: function(context) {
              const value = context.parsed.y
              const label = context.dataset.label
              return `${label}: ${value?.toFixed(2) || 'N/A'}`
            }
          }
        }
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: 'Time (newest on right)'
          }
        },
        y: {
          display: true,
          title: {
            display: true,
            text: 'Balance Line Value'
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      }
    }
  }
  
  try {
    globalChart.value = new Chart(globalChartCanvas.value, config)
  } catch (error) {
    console.error('Error creating global chart:', error)
  }
}

// Get dash pattern for different markets
const getMarketDashPattern = (market) => {
  const patterns = {
    'points': [],
    'total_rebounds': [5, 5],
    'assists': [10, 5],
    'three_point_field_goal': [5, 10, 5, 10],
    'turnovers': [15, 5],
    'blocks': [5, 5, 5, 15],
    'steals': [20, 5],
    'pra': [10, 10],
    'pr': [5, 15],
    'pa': [15, 5, 5, 5],
    'ra': [5, 5, 15, 5],
    'bs': [10, 5, 5, 10]
  }
  return patterns[market] || []
}

// Get player color for global chart
const getPlayerColor = (index) => {
  const colors = [
    '#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6',
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1',
    '#14b8a6', '#eab308', '#dc2626', '#16a34a', '#ca8a04'
  ]
  return colors[index % colors.length]
}

// Generate global stepped line path
const getGlobalSteppedLinePath = (timeline, playerIndex) => {
  if (!timeline || timeline.length === 0) return ''
  
  const reversedTimeline = timeline.slice().reverse() // Newest on right
  let path = ''
  
  reversedTimeline.forEach((entry, index) => {
    const x = (index / (reversedTimeline.length - 1)) * 1160 + 20
    const y = 280 - ((entry.balance_line || 0) * 2 + 10)
    
    if (index === 0) {
      path += `M ${x} ${y}`
    } else {
      path += ` H ${x} V ${y}`
    }
  })
  
  return path
}

// Generate SVG path points for stepped line chart
const getSteppedLinePath = (field) => {
  if (!selectedMarketData.value || !selectedMarketData.value.timeline.length) return ''
  
  const timeline = selectedMarketData.value.timeline.slice().reverse() // Newest on right
  let path = ''
  
  timeline.forEach((entry, index) => {
    const x = (index / (timeline.length - 1)) * 760 + 20
    let y
    
    if (field === 'balance_line') {
      y = 200 - ((entry.balance_line || 0) * 2 + 10)
    } else if (field === 'balance_line_over_odds') {
      y = 200 - ((entry.balance_line_over_odds || 0) * 10 + 5)
    } else if (field === 'balance_line_under_odds') {
      y = 200 - ((entry.balance_line_under_odds || 0) * 10 + 5)
    } else {
      y = 100 // Default center
    }
    
    if (index === 0) {
      path += `M ${x} ${y}`
    } else {
      // Create stepped line: horizontal first, then vertical
      const prevX = ((index - 1) / (timeline.length - 1)) * 760 + 20
      path += ` H ${x} V ${y}`
    }
  })
  
  return path
}

// Cleanup charts when component unmounts
onUnmounted(() => {
  if (marketChart.value) {
    marketChart.value.destroy()
  }
  if (globalChart.value) {
    globalChart.value.destroy()
  }
})

// Time navigation methods
const previousTimestamp = () => {
  if (currentTimestampIndex.value > 0) {
    currentTimestampIndex.value--
    currentTimestamp.value = availableTimestamps.value[currentTimestampIndex.value]
  }
}

const nextTimestamp = () => {
  if (currentTimestampIndex.value < availableTimestamps.value.length - 1) {
    currentTimestampIndex.value++
    currentTimestamp.value = availableTimestamps.value[currentTimestampIndex.value]
  }
}

const jumpToTimestamp = async (index) => {
  currentTimestampIndex.value = index
  currentTimestamp.value = availableTimestamps.value[index]
  
  // Fetch data for this specific timestamp
  await loadDataForTimestamp(currentTimestamp.value)
}

        const loadDataForTimestamp = async (timestamp) => {
          if (!props.fixtureId || !timestamp) return
          
          loading.value = true
          error.value = null
          
          try {
            const params = new URLSearchParams()
            params.append('start_date', timestamp)
            params.append('end_date', timestamp)
            
            if (filters.value.marketType) params.append('market_type', filters.value.marketType)
            if (filters.value.playerId) params.append('player_id', filters.value.playerId)
            if (filters.value.isBalanced !== '') params.append('is_balanced', filters.value.isBalanced)
            
            const response = await axios.get(`/api/timeline-clickhouse/${props.fixtureId}?${params.toString()}`)
    
    if (response.data.success) {
      timelineData.value = response.data.data || []
      summary.value = response.data.meta || {}
    } else {
      throw new Error(response.data.message || 'Failed to load timeline data')
    }
  } catch (err) {
    console.error('Error loading timestamp data:', err)
    error.value = err.response?.data?.message || err.message || 'Failed to load timestamp data'
  } finally {
    loading.value = false
  }
}

const getStatusColor = (status) => {
  const colors = {
    'active': 'text-green-600',
    'closed': 'text-red-600',
    'draft': 'text-yellow-600',
    'suspended': 'text-orange-600'
  }
  return colors[status] || 'text-gray-600'
}

const loadFixtureDetails = async () => {
  loadingFixture.value = true
  
  try {
    const response = await axios.get(`/api/timeline-clickhouse/fixtures/list`)
    
    if (response.data && response.data.success) {
      fixturesList.value = response.data.data
      
      // Find the current fixture from the list
      const currentFixture = response.data.data.find(f => f.id == props.fixtureId)
      if (currentFixture) {
        fixtureDetails.value = currentFixture
      }
    } else {
      throw new Error('No fixture data received')
    }
  } catch (err) {
    console.error('Error loading fixture details:', err)
    fixturesList.value = []
    fixtureDetails.value = {}
  } finally {
    loadingFixture.value = false
  }
}

const selectFixture = async (fixture) => {
  fixtureDetails.value = fixture
  
  // Close the fixture details popup
  showFixtureDetails.value = false
  
  // Reload timeline data for the new fixture within the same view
  if (fixture.id !== props.fixtureId) {
    await loadTimelineDataForFixture(fixture.id)
  }
}

const resetToOriginalFixture = async () => {
  // Reset to the original fixture
  currentFixtureId.value = props.fixtureId
  await loadTimelineData()
}

const getDataDensityStats = () => {
  let rich = 0
  let good = 0
  let limited = 0
  
  groupedData.value.forEach(player => {
    Object.keys(player.markets).forEach(marketType => {
      const timelineLength = player.markets[marketType].timeline?.length || 0
      const balancedLength = player.markets[marketType].balancedLines?.length || 0
      
      // Use the total timeline length for density calculation
      if (timelineLength >= 10) {
        rich++
      } else if (timelineLength >= 5) {
        good++
      } else if (timelineLength > 0) {
        limited++
      }
    })
  })
  
  return { rich, good, limited }
}

const getMarketDisplayName = (market) => {
  const marketNames = {
    'points': 'Points',
    'total_rebounds': 'Rebounds',
    'assists': 'Assists',
    'three_point_field_goal': '3 Points',
    'turnovers': 'Turnovers',
    'blocks': 'Blocks',
    'steals': 'Steals',
    'pra': 'PRA',
    'pr': 'PR',
    'pa': 'PA',
    'ra': 'RA',
    'bs': 'BS'
  }
  return marketNames[market] || market.replace('_', ' ')
}

const loadTimelineDataForFixture = async (newFixtureId) => {
  if (!newFixtureId) return
  
  loading.value = true
  error.value = null
  
  try {
    const params = new URLSearchParams()
    
    if (filters.value.marketType) params.append('market_type', filters.value.marketType)
    if (filters.value.playerId) params.append('player_id', filters.value.playerId)
    if (filters.value.startDate) params.append('start_date', filters.value.startDate)
    if (filters.value.endDate) params.append('end_date', filters.value.endDate)
    if (filters.value.isBalanced !== '') params.append('is_balanced', filters.value.isBalanced)
    
    const response = await axios.get(`/api/timeline-clickhouse/${newFixtureId}?${params.toString()}`)
    
    if (response.data.success) {
      timelineData.value = response.data.data || []
      summary.value = response.data.meta || {}
      playersList.value = response.data.meta?.players_list || {}
      fixtureData.value = response.data.meta?.fixture_data || {}
      
      // Update current fixture ID
      currentFixtureId.value = newFixtureId
      
      // Extract unique timestamps and sort them (newest first)
      const timestamps = [...new Set(timelineData.value.map(entry => entry.created_at))]
        .sort((a, b) => new Date(b) - new Date(a))
      
      availableTimestamps.value = timestamps
      currentTimestampIndex.value = 0 // Start with latest (now at index 0)
      currentTimestamp.value = timestamps[0]
    } else {
      throw new Error(response.data.message || 'Failed to load timeline data')
    }
  } catch (err) {
    console.error('Error loading timeline data for fixture:', err)
    error.value = err.response?.data?.message || err.message || 'Failed to load timeline data'
  } finally {
    loading.value = false
  }
}

// Watchers
watch(() => props.show, (newValue) => {
  if (newValue && props.fixtureId) {
    loadTimelineData()
  }
})

watch(() => showFixtureDetails.value, (newValue) => {
  if (newValue && props.fixtureId) {
    loadFixtureDetails()
  }
})

watch(() => showSidepanel.value, (newValue) => {
  if (newValue && fixturesList.value.length === 0) {
    loadFixtureDetails()
  }
})

watch(() => props.fixtureId, (newValue) => {
  if (props.show && newValue) {
    loadTimelineData()
  }
})

// Watch for target time changes and auto-apply filter
watch(() => filters.value.targetTime, (newValue) => {
  if (newValue && availableTimestamps.value.length > 0) {
    const closestTimestamp = findClosestPastTimestamp(newValue, availableTimestamps.value)
    if (closestTimestamp) {
      const closestIndex = availableTimestamps.value.findIndex(ts => ts === closestTimestamp)
      currentTimestampIndex.value = closestIndex
      currentTimestamp.value = closestTimestamp
    }
  }
  // Reset target time filter when target time changes
  showTargetTimeData.value = false
})

// Lifecycle
onMounted(() => {
  if (props.show && props.fixtureId) {
    loadTimelineData()
  }
})
</script>

<style scoped>
/* Custom scrollbar */
.overflow-auto::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.overflow-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Horizontal scrollbar for table */
.overflow-x-auto::-webkit-scrollbar {
  height: 8px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Ensure table cells have consistent width */
.grid-cols-12 > div {
  min-width: 180px;
  width: 180px;
}

/* Market cell styling for better spacing */
.market-cell {
  min-width: 180px;
  width: 180px;
  padding: 12px;
}

/* Improve text spacing in market cells */
.market-cell .text-xs {
  line-height: 1.2;
  margin-bottom: 2px;
}

/* Better spacing for over/under sections */
.market-cell .flex {
  gap: 4px;
}
</style>
